import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { statements } from "@/api/statements";
import { useActiveAccount } from "@/components/ActiveAccount";
import { downloadCsv } from "@/lib/csv";
import { Button, Card, CardBody, CardHeader, EmptyState, Input, Label } from "@/components/ui";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtMoney(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function StatementsPage() {
  const { active, accounts, setActiveId, isLoading: accountsLoading } = useActiveAccount();
  const [date, setDate] = useState<string>(todayIso());

  const stmt = useQuery({
    queryKey: ["statement", active?.id, date],
    queryFn: () => statements.get(active!.id, date),
    enabled: !!active && !!date,
    retry: false,
  });

  const totals = useMemo(() => {
    if (!stmt.data) return null;
    const fillCount = stmt.data.fills.length;
    const grossNotional = stmt.data.fills.reduce(
      (acc, f) => acc + Math.abs(Number(f.price) * Number(f.quantity) || 0),
      0,
    );
    return { fillCount, grossNotional };
  }, [stmt.data]);

  if (accountsLoading) {
    return <p className="text-sm text-slate-500">Loading accounts…</p>;
  }
  if (!accounts || accounts.length === 0) {
    return (
      <EmptyState
        title="No trading accounts"
        description="Create a trading account before viewing daily statements."
      />
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Daily statement</h1>

      <Card>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="acct">Trading account</Label>
            <select
              id="acct"
              value={active?.id ?? ""}
              onChange={(e) => setActiveId(e.target.value)}
              className="mt-1 block w-full rounded border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.login_code} ({a.base_currency})
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="date">Statement date (UTC)</Label>
            <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="secondary"
              disabled={!stmt.data}
              onClick={() => stmt.data && downloadCsv(stmt.data.fills, `fills-${stmt.data.date}`)}
            >
              Export fills CSV
            </Button>
            <Button
              variant="secondary"
              disabled={!stmt.data}
              onClick={() => stmt.data && downloadCsv(stmt.data.ledger, `ledger-${stmt.data.date}`)}
            >
              Export ledger CSV
            </Button>
          </div>
        </CardBody>
      </Card>

      {stmt.isLoading && <p className="text-sm text-slate-500">Loading statement…</p>}
      {stmt.isError && (
        <Card>
          <CardBody>
            <p className="text-sm text-red-500">
              {stmt.error instanceof ApiError
                ? stmt.error.message.replace(/^\d+:\s*/, "")
                : "Failed to load statement."}
            </p>
          </CardBody>
        </Card>
      )}

      {stmt.data && (
        <>
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold">Summary</h2>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-xs uppercase text-slate-500">Starting balance</dt>
                  <dd className="tnum">{fmtMoney(stmt.data.starting_balance)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Ending balance</dt>
                  <dd className="tnum font-medium">{fmtMoney(stmt.data.ending_balance)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Realized P&amp;L</dt>
                  <dd
                    className={`tnum ${Number(stmt.data.pnl_realized) >= 0 ? "text-emerald-500" : "text-red-500"}`}
                  >
                    {fmtMoney(stmt.data.pnl_realized)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Commissions</dt>
                  <dd className="tnum">{fmtMoney(stmt.data.commissions)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Swap</dt>
                  <dd className="tnum">{fmtMoney(stmt.data.swaps)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Deposits</dt>
                  <dd className="tnum">{fmtMoney(stmt.data.deposits)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-slate-500">Withdrawals</dt>
                  <dd className="tnum">{fmtMoney(stmt.data.withdrawals)}</dd>
                </div>
                {totals && (
                  <div>
                    <dt className="text-xs uppercase text-slate-500">Fills · gross</dt>
                    <dd className="tnum">
                      {totals.fillCount} · {totals.grossNotional.toLocaleString()}
                    </dd>
                  </div>
                )}
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold">Fills ({stmt.data.fills.length})</h2>
            </CardHeader>
            <CardBody className="overflow-x-auto p-0">
              {stmt.data.fills.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">No fills on this date.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Symbol</th>
                      <th className="px-3 py-2">Side</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Price</th>
                      <th className="px-3 py-2 text-right">Commission</th>
                      <th className="px-3 py-2">Venue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmt.data.fills.map((f, i) => (
                      <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                        <td className="px-3 py-2 tnum text-slate-500">
                          {new Date(f.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="px-3 py-2 font-medium">{f.symbol}</td>
                        <td
                          className={`px-3 py-2 ${f.side === "BUY" ? "text-emerald-500" : "text-red-500"}`}
                        >
                          {f.side}
                        </td>
                        <td className="px-3 py-2 text-right tnum">{f.quantity}</td>
                        <td className="px-3 py-2 text-right tnum">{f.price}</td>
                        <td className="px-3 py-2 text-right tnum">{f.commission}</td>
                        <td className="px-3 py-2 text-slate-500">{f.venue ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold">Ledger ({stmt.data.ledger.length})</h2>
            </CardHeader>
            <CardBody className="overflow-x-auto p-0">
              {stmt.data.ledger.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">No ledger entries on this date.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Currency</th>
                      <th className="px-3 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmt.data.ledger.map((l, i) => (
                      <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                        <td className="px-3 py-2 tnum text-slate-500">
                          {new Date(l.occurred_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">{l.entry_type}</td>
                        <td
                          className={`px-3 py-2 text-right tnum ${Number(l.amount) >= 0 ? "text-emerald-500" : "text-red-500"}`}
                        >
                          {l.amount}
                        </td>
                        <td className="px-3 py-2">{l.currency}</td>
                        <td className="px-3 py-2 text-slate-500">{l.note ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold">
                End-of-day positions ({stmt.data.positions_eod.length})
              </h2>
            </CardHeader>
            <CardBody className="overflow-x-auto p-0">
              {stmt.data.positions_eod.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">No open positions at EOD.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Symbol</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Avg entry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stmt.data.positions_eod.map((p, i) => (
                      <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                        <td className="px-3 py-2 font-medium">{p.symbol}</td>
                        <td className="px-3 py-2 text-right tnum">{p.qty}</td>
                        <td className="px-3 py-2 text-right tnum">{p.avg_entry}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
